import { PAGE_ROLES, ROLE_LABELS } from '@bo/constants/userRoles';
import { useBoAuth } from '@boContexts/BoAuthContext';
import BoAuthService from '@boServices/boAuthService';
import { faBooks, faBuilding, faUserGroup } from '@fortawesome/pro-regular-svg-icons';
import Menu from '@sharedComponents/Menu';
import logo from '@src/assets/logo_black.svg';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const authService = new BoAuthService();

/**
 * Backoffice wrapper around the agnostic `Menu`. Builds the nav entries from the current BO
 * routes and wires them to react-router navigation; entries requiring a role are filtered out for
 * users who don't hold it (expanded through the BO role hierarchy). The footer wires "Disconnect"
 * (destroys the session via POST /auth/logout then reloads /login), "Settings" (routes to
 * /parameters) and the current user's name / role, fetched
 * from the auth context. "Help" is not wired.
 *
 * Controlled collapse: `isOpen`/`onToggle` are owned by the layout so it can offset the page
 * content past the fixed collapsed rail.
 */
const BoMenu = ({ isOpen = true, onToggle = () => {} }) => {
    const { t } = useTranslation('bo');
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { user, hasRole } = useBoAuth();

    const allNavItems = [
        {
            id: 'clients',
            label: t('Clients'),
            icon: faBuilding,
            active: pathname.startsWith('/clients'),
            action: () => navigate('/clients'),
        },
        {
            id: 'catalogue',
            label: t('Catalogue'),
            icon: faBooks,
            subItems: [
                {
                    id: 'collections',
                    label: t('Collections'),
                    active: pathname.startsWith('/collections'),
                    action: () => navigate('/collections'),
                    requiredRole: PAGE_ROLES.collections,
                },
                {
                    id: 'tests',
                    label: t('Tests'),
                    active: pathname.startsWith('/tests'),
                    action: () => navigate('/tests'),
                    requiredRole: PAGE_ROLES.tests,
                },
                {
                    id: 'tags',
                    label: t('Tags'),
                    active: pathname.startsWith('/tags'),
                    action: () => navigate('/tags'),
                    requiredRole: PAGE_ROLES.tags,
                },
            ],
        },
        {
            id: 'users',
            label: t('Users'),
            icon: faUserGroup,
            active: pathname.startsWith('/users'),
            action: () => navigate('/users'),
            requiredRole: PAGE_ROLES.users,
        },
    ];
    const canAccess = (item) => !item.requiredRole || hasRole(item.requiredRole);
    // A group is dropped when none of its sub-items is allowed, and is active when one of them is.
    const navItems = allNavItems
        .map((item) => {
            if (!item.subItems) return item;
            const subItems = item.subItems.filter(canAccess);
            return { ...item, subItems, active: subItems.some((sub) => sub.active) };
        })
        .filter((item) => (item.subItems ? item.subItems.length > 0 : canAccess(item)));

    const footer = {
        settingsAction: () => navigate('/parameters'),
        settingsActive: pathname.startsWith('/parameters'),
        disconnectAction: async () => {
            // Real logout: destroy the server session, then hard-reload to /login so all in-memory
            // state is cleared and the auth context re-evaluates against the now-invalid session.
            try {
                await authService.logout();
            } finally {
                window.location.assign(`${import.meta.env.BASE_URL}login`);
            }
        },
        user: {
            firstName: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
            roleLabel: (user?.roles ?? []).map((role) => t(ROLE_LABELS[role] ?? role)).join(', '),
        },
    };

    return <Menu logo={logo} navItems={navItems} footer={footer} isOpen={isOpen} onToggle={onToggle} />;
};

export default BoMenu;
